import React, {
  useMemo,
  useRef,
  useCallback,
  useContext,
  useEffect,
  useState,
  Suspense,
} from "react";
import { styled } from "@mui/material/styles";
import { Typography, CircularProgress } from "@mui/material";
import useAutoSave from "../../../../hooks/useAutoSave";
import useProjectStore from "../../../ProjectCanvas/useProjectStore";
import { storeMap } from "../../../../utils/prefillComponentStores";
import { computePlaceholdersForComponent } from "../../../../utils/placeholderExtractor";
import {
  computePlaceholdersForComponentFromOutputMap,
  mergePlaceholderResponses,
} from "../../../../utils/placeholderParity";
import { CanvasContext } from "../WorkflowCanvas";
import { getInlineConfig } from "../../../InlineNodeConfigs";
import { loadComponentConfig } from "../../../../utils/loadComponentConfig";

const PanelRoot = styled("div")({
  width: "100%",
  overflowX: "hidden",
  padding: "12px 16px 16px",
});

const SaveIndicator = styled("div")(({ theme }) => ({
  position: "absolute",
  bottom: 4,
  left: 6,
  display: "inline-flex",
  alignItems: "center",
  gap: 2,
  fontSize: "7px",
  fontWeight: 500,
  letterSpacing: "0.01em",
  color: theme.palette.text.disabled,
  backgroundColor:
    theme.palette.mode === "dark"
      ? "rgba(255,255,255,0.04)"
      : "rgba(0,0,0,0.03)",
  backdropFilter: "blur(4px)",
  borderRadius: 3,
  padding: "1px 4px",
  zIndex: 5,
  pointerEvents: "none",
  transition: "opacity 0.2s ease",
  transform: "scale(0.65)",
  transformOrigin: "bottom left",
}));

const ComponentIdLabel = styled("div")(({ theme }) => ({
  position: "absolute",
  bottom: 4,
  right: 6,
  display: "inline-flex",
  alignItems: "center",
  fontSize: "7px",
  fontWeight: 500,
  letterSpacing: "0.01em",
  color: theme.palette.text.disabled,
  backgroundColor:
    theme.palette.mode === "dark"
      ? "rgba(255,255,255,0.04)"
      : "rgba(0,0,0,0.03)",
  backdropFilter: "blur(4px)",
  borderRadius: 3,
  padding: "1px 4px",
  zIndex: 5,
  cursor: "pointer",
  transition: "opacity 0.2s ease",
  transform: "scale(0.65)",
  transformOrigin: "bottom right",
  "&:hover": {
    color: theme.palette.text.secondary,
    backgroundColor:
      theme.palette.mode === "dark"
        ? "rgba(255,255,255,0.08)"
        : "rgba(0,0,0,0.06)",
  },
}));

const LoadingFallback = (
  <div style={{ padding: 16, textAlign: "center" }}>
    <CircularProgress size={20} />
  </div>
);

function hasInlineComponentData(component) {
  if (!component || component.isCanvasViewShell) return false;
  return !!(component.rawComponentData || component.componentData);
}

/**
 * InlineConfigPanel renders a component's config directly on the ReactFlow node.
 *
 * Looks up the InlineNodeConfigs registry for a dedicated, compact NodeUI-based
 * config view. Components without an inline config show a "not configured" message.
 *
 * Auto-save is handled by subscribing to the keyed Zustand store.
 * Config is always visible on the node (Retool-style), not behind a click-to-expand.
 */
const InlineConfigPanel = ({
  componentId,
  componentType,
  projectId,
  version,
  nodeData,
  readOnly = false,
}) => {
  const panelRef = useRef(null);
  const initTimerRef = useRef(null);
  const [storeReady, setStoreReady] = useState(false);
  const [storeLoaded, setStoreLoaded] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const fetchAttemptedRef = useRef(false);

  const noopSave = useCallback(() => {}, []);
  const {
    triggerSave: _triggerSave,
    saveStatus,
    lastSavedAt,
  } = useAutoSave(componentId, componentType);
  const triggerSave = readOnly ? noopSave : _triggerSave;

  const {
    allPlaceholdersInWorkflow,
    placeholderMapSource,
    globalPlaceholders,
    credentialPlaceholders,
  } = useContext(CanvasContext);

  // ─── Ensure store is loaded (prefill or REST fallback) ───────────────
  // If prefillComponentStores didn't populate this store (e.g. rawComponentData
  // was null in the GraphQL response), fetch from REST as a fallback.
  //
  // Component types with setter=null (form, sequential_form, form_section) never
  // populate a keyed store — their inline preview reads directly from
  // useProjectStore.components. If the canvas only has a lightweight shell,
  // fetch/merge the component detail first so those previews are not empty.
  useEffect(() => {
    setLoadError(null);
    fetchAttemptedRef.current = false;
    const config = storeMap[componentType];
    if (!config?.getStore) {
      setStoreReady(true);
      return;
    }

    if (!config.setter) {
      const existing = (useProjectStore.getState().components || []).find(
        (component) => component.componentId === componentId,
      );

      if (hasInlineComponentData(existing) || !projectId) {
        setStoreReady(true);
        return;
      }

      if (fetchAttemptedRef.current) return;
      fetchAttemptedRef.current = true;

      const workflowVersion =
        version ?? useProjectStore.getState().version ?? 0;
      let cancelled = false;
      setStoreReady(false);

      loadComponentConfig(componentId, {
        projectId,
        version: workflowVersion || null,
        componentType,
        source: "inline",
      })
        .then(() => {
          if (cancelled) return;
          setLoadError(null);
          setStoreReady(true);
        })
        .catch((error) => {
          if (cancelled) return;
          setLoadError(error);
          setStoreReady(true);
        });

      return () => {
        cancelled = true;
      };
    }

    const store = config.getStore(componentId);

    if (store.getState().loaded) {
      setStoreReady(true);
      return;
    }

    if (fetchAttemptedRef.current) return;
    fetchAttemptedRef.current = true;

    const workflowVersion = version ?? useProjectStore.getState().version ?? 0;
    let cancelled = false;

    loadComponentConfig(componentId, {
      projectId,
      version: workflowVersion || null,
      componentType,
      source: "inline",
    })
      .then(() => {
        if (cancelled) return;
        setLoadError(null);
        setStoreLoaded(true);
        setStoreReady(true);
      })
      .catch((error) => {
        if (cancelled) return;
        setLoadError(error);
        setStoreReady(true);
      });

    return () => {
      cancelled = true;
    };
  }, [componentId, componentType, projectId, version]);

  // ─── Watch for store.loaded changes (e.g. from ComponentInitializationUtil) ──
  // When a new component is dropped, initializeComponent runs async and
  // populates the keyed store AFTER this panel has already mounted.
  // This subscription detects that and triggers a re-render so auto-save arms.
  useEffect(() => {
    const config = storeMap[componentType];
    if (!config?.getStore) return;
    const store = config.getStore(componentId);

    if (store.getState().loaded) {
      setStoreLoaded(true);
      return;
    }

    const unsub = store.subscribe((state) => {
      if (state.loaded && !storeLoaded) {
        setStoreLoaded(true);
        setStoreReady(true);
      }
    });
    return unsub;
  }, [componentId, componentType, storeLoaded]);

  // ─── Auto-save on keyed store changes ───────────────────────────────
  // Subscribe to the keyed store so that ANY field change in the template
  // triggers a debounced save, without requiring individual template modifications.
  useEffect(() => {
    if (readOnly) return;
    if (!storeReady) return;

    const config = storeMap[componentType];
    if (!config?.getStore) return;

    const store = config.getStore(componentId);
    const storeState = store.getState();

    if (!storeState.loaded) return;
    if (!storeState._loadProtected) return;

    // Take a snapshot of the store state right after load.
    // Only trigger auto-save when the state actually differs from this snapshot,
    // which means the USER changed something (not just prefill or re-render).
    // For stores with a custom save() (e.g. API ResReq), the data is spread across
    // multiple fields (requestData, sampleData, etc.), so we skip the snapshot
    // comparison and rely on the 2s arming delay instead.
    const hasCustomSave = typeof storeState.save === "function";
    const snapshot = hasCustomSave
      ? null
      : JSON.stringify(storeState.data || {});

    // Skip the initial burst of state changes from data loading.
    // After 2s, start watching for user-driven edits.
    let armed = false;
    initTimerRef.current = setTimeout(() => {
      const s = store.getState();
      if (!s.loaded || !s._loadProtected) return;
      armed = true;
    }, 2000);

    const unsub = store.subscribe(() => {
      if (!armed) return;
      const s = store.getState();
      if (!s.loaded || !s._loadProtected) {
        armed = false;
        return;
      }
      if (snapshot) {
        const current = JSON.stringify(s.data || {});
        if (current === snapshot) return;
      }
      triggerSave();
    });

    return () => {
      clearTimeout(initTimerRef.current);
      unsub();
    };
  }, [
    componentId,
    componentType,
    triggerSave,
    storeReady,
    storeLoaded,
    readOnly,
  ]);

  // ─── Focus tracking for placeholder insertion routing ───────────────
  // When any field inside this panel gains focus, register this component
  // as the active target so placeholder insertions route here.
  const handleFocusCapture = useCallback(() => {
    useProjectStore.getState().setActiveComponentId(componentId);
  }, [componentId]);

  // When focus leaves this panel entirely, clear the active target after
  // a short delay (allows focus to move between fields within the same panel).
  const handleBlurCapture = useCallback(
    (e) => {
      // relatedTarget is the element receiving focus. If it's inside this panel, don't clear.
      const panel = panelRef.current;
      if (panel && e.relatedTarget && panel.contains(e.relatedTarget)) return;

      setTimeout(() => {
        if (useProjectStore.getState().activeComponentId === componentId) {
          useProjectStore.getState().setActiveComponentId(null);
          useProjectStore.getState().setActiveInputRef(null);
        }
      }, 150);
    },
    [componentId],
  );

  // Stop pointer events from propagating to ReactFlow (prevents pan/drag)
  const stopPropagation = (e) => {
    e.stopPropagation();
  };

  const [lastSavedLabel, setLastSavedLabel] = React.useState(null);
  const [copiedId, setCopiedId] = React.useState(false);
  React.useEffect(() => {
    if (!lastSavedAt) {
      setLastSavedLabel(null);
      return;
    }
    const update = () => {
      const secs = Math.round((Date.now() - lastSavedAt) / 1000);
      if (secs < 5) setLastSavedLabel("just now");
      else if (secs < 60) setLastSavedLabel(`${secs}s ago`);
      else setLastSavedLabel(`${Math.floor(secs / 60)}m ago`);
    };
    update();
    const id = setInterval(update, 10000);
    return () => clearInterval(id);
  }, [lastSavedAt]);

  // Compute available placeholders from upstream components.
  // Subscribe reactively so downstream nodes update when any component's
  // rawComponentData changes (e.g. after a form adds new questions).
  const storeComponents = useProjectStore((s) => s.components);
  const availablePlaceholders = useMemo(() => {
    const computed = computePlaceholdersForComponent(
      componentId,
      storeComponents,
      globalPlaceholders || [],
      credentialPlaceholders || [],
    );

    const canonical = computePlaceholdersForComponentFromOutputMap(
      componentId,
      storeComponents,
      allPlaceholdersInWorkflow || {},
      globalPlaceholders || [],
      credentialPlaceholders || [],
    );

    const result =
      placeholderMapSource === "backend-v2"
        ? mergePlaceholderResponses(canonical, computed)
        : computed;

    return result;
  }, [
    componentId,
    storeComponents,
    allPlaceholdersInWorkflow,
    placeholderMapSource,
    globalPlaceholders,
    credentialPlaceholders,
  ]);

  const InlineConfig = useMemo(
    () => getInlineConfig(componentType),
    [componentType],
  );

  if (!storeReady) {
    return (
      <PanelRoot>
        <div style={{ padding: 16, textAlign: "center" }}>
          <CircularProgress size={20} />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 1, fontSize: 10 }}
          >
            Loading config...
          </Typography>
        </div>
      </PanelRoot>
    );
  }

  if (loadError) {
    return (
      <PanelRoot>
        <Typography
          variant="caption"
          color="error"
          sx={{ p: 1, display: "block" }}
        >
          Config failed to load. Changes are disabled for this component.
        </Typography>
      </PanelRoot>
    );
  }

  return (
    <div
      onMouseDown={stopPropagation}
      onClick={stopPropagation}
      onPointerDown={stopPropagation}
      onWheel={stopPropagation}
      onFocusCapture={readOnly ? undefined : handleFocusCapture}
      onBlurCapture={readOnly ? undefined : handleBlurCapture}
      style={{
        position: "relative",
        pointerEvents: readOnly ? "none" : undefined,
      }}
    >
      {!readOnly && (
        <SaveIndicator>
          {saveStatus === "saving" && (
            <>
              <span
                style={{
                  width: 5,
                  height: 5,
                  borderRadius: "50%",
                  backgroundColor: "#f59e0b",
                  flexShrink: 0,
                }}
              />
              <span>saving</span>
            </>
          )}
          {saveStatus === "saved" && lastSavedLabel && (
            <>
              <span
                style={{
                  width: 5,
                  height: 5,
                  borderRadius: "50%",
                  backgroundColor: "#4caf50",
                  opacity: 0.7,
                  flexShrink: 0,
                }}
              />
              <span>saved {lastSavedLabel}</span>
            </>
          )}
          {saveStatus === "error" && (
            <>
              <span
                style={{
                  width: 5,
                  height: 5,
                  borderRadius: "50%",
                  backgroundColor: "#d32f2f",
                  opacity: 0.8,
                  flexShrink: 0,
                }}
              />
              <span style={{ color: "#d32f2f" }}>save failed</span>
            </>
          )}
        </SaveIndicator>
      )}
      <ComponentIdLabel
        role="button"
        tabIndex={0}
        onClick={(e) => {
          e.stopPropagation();
          navigator.clipboard
            .writeText(componentId)
            .then(() => {
              setCopiedId(true);
              setTimeout(() => setCopiedId(false), 1500);
            })
            .catch(() => {});
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            e.stopPropagation();
            navigator.clipboard
              .writeText(componentId)
              .then(() => {
                setCopiedId(true);
                setTimeout(() => setCopiedId(false), 1500);
              })
              .catch(() => {});
          }
        }}
        onMouseDown={(e) => e.stopPropagation()}
        title="Click to copy component ID"
        aria-label={`Copy component ID ${componentId}`}
        style={copiedId ? { color: "#4caf50" } : undefined}
      >
        {componentId}
      </ComponentIdLabel>
      <PanelRoot ref={panelRef} className="nodrag nowheel">
        {InlineConfig ? (
          <Suspense fallback={LoadingFallback}>
            <InlineConfig
              componentId={componentId}
              componentType={componentType}
              projectId={projectId}
              version={version}
              nodeData={nodeData}
              availablePlaceholders={availablePlaceholders}
              triggerSave={triggerSave}
            />
          </Suspense>
        ) : (
          <Typography variant="caption" color="text.secondary" sx={{ p: 1 }}>
            No inline config for this component type
          </Typography>
        )}
      </PanelRoot>
    </div>
  );
};

export default React.memo(InlineConfigPanel);
